import MuiModal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import CancelIcon from '@/assets/cancel2.svg?react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash';
import { useAuthContext } from '@/contexts/AuthContext';
import AllSearchTabPanel from './GlobalSearchTabPanels/AllSearchTabPanel';
import GamesSearchTabPanel from './GlobalSearchTabPanels/GamesSearchTabPanel';
import ComplianceDocumentsSearchTabPanel from './GlobalSearchTabPanels/ComplianceDocumentsSearchTabPanel';
import MarketingAssetsSearchTabPanel from './GlobalSearchTabPanels/MarketingAssetsSearchTabPanel';
import PartnersSearchTabPanel from './GlobalSearchTabPanels/PartnersSearchTabPanel';
import {
  EDIT_CONTACTS_PERMISSIONS,
  VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS,
} from '@/constants/Permissions';
import {
  GlobalSearchResultsInfo,
  GlobalSearchScope,
} from './GlobalSearchTabPanels/GlobalSearchTabPanelTypes';
import { ga } from '@/lib/analytics';
import BasketBar from '@/components/ShareBasketModal/BasketBar';
import ShareBasketModal from '@/components/ShareBasketModal/ShareBasketModal';
import { SharedBasketItemCategory } from '@/lib/types';

export type GlobalSearchModalProps = {
  open: boolean;
  onClose: () => void;
  titleId?: string;
};

const GlobalSearchModal = ({
  open,
  onClose,
}: GlobalSearchModalProps) => {
  const { user } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] =
    useState('');
  const [activeTab, setActiveTab] =
    useState<GlobalSearchScope>('all');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [loadedFiles, setLoadedFiles] = useState<{
    ids: number[];
    total?: number;
  }>({ ids: [] });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchAcceptSentRef = useRef<string | null>(null);
  const debouncedSearchChange = useMemo(
    () =>
      debounce(
        (value: string) => setDebouncedSearchQuery(value),
        500,
      ),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  useEffect(() => {
    if (open) return;
    debouncedSearchChange.cancel();
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setActiveTab('all');
    searchAcceptSentRef.current = null;
  }, [open, debouncedSearchChange]);

  const handleTabChange = useCallback((tab: GlobalSearchScope) => {
    setActiveTab(tab);
    setLoadedFiles({ ids: [] });
    ga.trackTabView({
      tab_name: tab,
      page: ga.PAGES.globalSearch,
    });
  }, []);

  /**
   * A global search has no explicit submit, so `search_accept` is reported
   * once the results for a (debounced) term and scope have loaded — one
   * event per term/scope combination.
   */
  const handleResultsLoaded = useCallback(
    ({ query, scope, resultCount }: GlobalSearchResultsInfo) => {
      if (!query) return;

      const key = `${scope}|${query}`;
      if (searchAcceptSentRef.current === key) return;
      searchAcceptSentRef.current = key;

      ga.trackSearchAccept({
        search_term: query,
        search_scope: scope,
        result_count: resultCount,
        page: ga.PAGES.globalSearch,
      });
    },
    [],
  );

  useEffect(() => {
    if (!open) return;
    const frameId = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [open]);

  const canSeePartnersTab = EDIT_CONTACTS_PERMISSIONS.every((key) =>
    (user?.permissions ?? []).some((permission) => permission.key === key),
  );

  const canSeeComplianceDocumentsTab =
    VIEW_COMPLIANCE_DOCUMENTS_PERMISSIONS.every((key) =>
      (user?.permissions ?? []).some(
        (permission) => permission.key === key,
      ),
    );

  const tabs = useMemo(
    () => [
      { value: 'all', label: 'All' },
      { value: 'games', label: 'Games' },
      ...(canSeeComplianceDocumentsTab
        ? [
            {
              value: 'compliance-documents',
              label: 'Compliance Documents',
            },
          ]
        : []),
      {
        value: 'marketing-assets',
        label: 'Marketing Assets',
      },
      ...(canSeePartnersTab
        ? [{ value: 'partners', label: 'Partners' }]
        : []),
    ],
    [canSeeComplianceDocumentsTab, canSeePartnersTab],
  );

  const safeActiveTab = tabs.some((tab) => tab.value === activeTab)
    ? activeTab
    : tabs[0].value;
  const hasSearchQuery = searchQuery.trim().length > 0;

  const renderTabContent = () => {
    switch (safeActiveTab) {
      case 'games':
        return (
          <GamesSearchTabPanel
            query={debouncedSearchQuery}
            scrollContainerRef={scrollRef}
            onResultNavigate={onClose}
            onResultsLoaded={handleResultsLoaded}
          />
        );
      case 'compliance-documents':
        return (
          <ComplianceDocumentsSearchTabPanel
            query={debouncedSearchQuery}
            scrollContainerRef={scrollRef}
            onResultNavigate={onClose}
            onResultsLoaded={handleResultsLoaded}
            onLoadedFilesChange={setLoadedFiles}
          />
        );
      case 'marketing-assets':
        return (
          <MarketingAssetsSearchTabPanel
            query={debouncedSearchQuery}
            scrollContainerRef={scrollRef}
            onResultNavigate={onClose}
            onResultsLoaded={handleResultsLoaded}
            onLoadedFilesChange={setLoadedFiles}
          />
        );
      case 'partners':
        return (
          <PartnersSearchTabPanel
            query={debouncedSearchQuery}
            scrollContainerRef={scrollRef}
            onResultNavigate={onClose}
            onResultsLoaded={handleResultsLoaded}
          />
        );
      case 'all':
      default:
        return (
          <AllSearchTabPanel
            query={debouncedSearchQuery}
            onRequestTabChange={handleTabChange}
            showPartnersSection={canSeePartnersTab}
            showComplianceDocumentsSection={
              canSeeComplianceDocumentsTab
            }
            onResultNavigate={onClose}
            onResultsLoaded={handleResultsLoaded}
          />
        );
    }
  };

  return (
    <MuiModal
      open={open}
      onClose={onClose}
      disableAutoFocus
      aria-labelledby="global-search-title"
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(10px)',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
    >
      <SearchModalRoot role="dialog" aria-modal="true">
        <SearchModalHeader>
          <SearchModalTitle id="global-search-title">
            Search everywhere
          </SearchModalTitle>
          <IconButton
            onClick={onClose}
            aria-label="Close global search"
            sx={{ padding: 0, width: 28, height: 28 }}
          >
            <CancelIcon />
          </IconButton>
        </SearchModalHeader>
        <SearchInput
          fullWidth
          inputRef={searchInputRef}
          value={searchQuery}
          placeholder="Search across portal…"
          onChange={(event) => {
            const nextQuery = event.target.value;
            setSearchQuery(nextQuery);
            debouncedSearchChange(nextQuery);
            setActiveTab('all');
          }}
          aria-label="Global search query"
        />
        <SearchTabs
          value={safeActiveTab}
          onChange={(_, value) => handleTabChange(value)}
          aria-label="Global search sections"
          variant="scrollable"
          scrollButtons={false}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              disabled={!hasSearchQuery}
            />
          ))}
        </SearchTabs>
        <SearchModalBody className="wide-scrollbar" ref={scrollRef}>
          {renderTabContent()}
        </SearchModalBody>
        <BasketBar
          variant="footer"
          category={BasketCategoryByTab[safeActiveTab]}
          loadedIds={loadedFiles.ids}
          totalCount={loadedFiles.total}
          onShareClick={() => setShareOpen(true)}
        />
        <ShareBasketModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      </SearchModalRoot>
    </MuiModal>
  );
};

// Keyed by string rather than GlobalSearchScope: `tabs` is assembled from
// plain literals, so `safeActiveTab` widens to string.
const BasketCategoryByTab: Record<
  string,
  SharedBasketItemCategory | undefined
> = {
  'compliance-documents': 'compliance_documents',
  'marketing-assets': 'marketing_assets',
};

const SearchModalRoot = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  height: '90%',
  maxWidth: 1270,
  maxHeight: 700,
  borderRadius: theme.radii.sm,
  backgroundColor: theme.palette.white,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
  boxShadow: '0px 24px 48px rgba(0, 0, 0, 0.2)',
}));

const SearchModalHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  gap: theme.spacing(2),
}));

const SearchModalTitle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  fontSize: 24,
  lineHeight: '28px',
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
}));

const SearchModalBody = styled('div')({
  height: '100%',
  overflowY: 'auto',
  flexGrow: 1,
  paddingTop: 16,
});

const SearchInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const SearchTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey200}`,
  marginBottom: theme.spacing(1),
  minHeight: 40,
  '& .MuiTab-root': {
    minHeight: 40,
  },
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: 3,
    bottom: -1,
  },
}));

export default GlobalSearchModal;
